import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import InlineGroup from 'components/Common/InlineGroup'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import FlashTourTravelUntilMessage from './FlashTourTravelUntilMessage'

import TextButton from 'components/Luxkit/Button/TextButton'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import Image from 'components/Common/Image'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import TextLink from 'components/Luxkit/TextLink'
import OfferDescription from '../OfferDescription'
import config from 'constants/config'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseSupportBanner from 'components/Cruises/CruiseOfferPage/content/CruiseSupportBanner'
import ContactUSP from '../ContactUSP'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { LANDING_PAGE_AVAILABLE } from 'constants/cruise'
import Clickable from 'components/Common/Clickable'
import { fetchCruiseLines } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

interface Props {
  offer: App.TourOffer;
  shareConfig: ReturnType<typeof getShareModalConfig>
  cruiseLines: Array<App.CruiseLine>;
}

const OfferTitle = styled(Heading)`
  margin-top: ${rem(10)};
`

const TravelUntilMessageWrapper = styled.div`
  margin-top: ${rem(20)};
`

const StyledOfferDescription = styled(OfferDescription)`
  margin-top: ${rem(20)};
`

const ButtonBar = styled(InlineGroup)`
  margin-top: ${rem(20)};
`

const OfferHandPickInclusionsTitle = styled(Subtitle)`
  margin-top: ${rem(16)};
`

const OfferMarkdownRender = styled(MarkdownRender)`
  margin-top: ${rem(16)};
`

const TourLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const TourInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const OfferBadgeTagWrapper = styled.div`
  margin-top: ${rem(16)};
`

function FlashTourIntro(props: Props) {
  const {
    offer,
    cruiseLines,
  } = props

  const {
    additionalDescription,
    description,
    inclusionsHeading,
    inclusions,
    name,
    travelToDate,
    type,
    vimeoVideoId,
    lowestPricePackage,
    location,
    holidayTypes,
    vendorName,
  } = offer

  const showLightbox = useContext(LightBoxContext)
  const showVideo = () => {
    showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
  }

  const dispatch = useAppDispatch()
  const offerPageTruncateText = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.offerPageTruncateText)

  const cruiseLine = useMemo(() => {
    const cruiseLine = cruiseLines.find(({ name }) => name === vendorName)
    return cruiseLine
  }, [cruiseLines, vendorName])

  const getItemLink = useCallback((cruiseLine ?: App.CruiseLine) : string => {
    if (!cruiseLine?.name || !cruiseLine?.code) return `/search/cruises?cruiseLines=${vendorName}`
    if (LANDING_PAGE_AVAILABLE.includes(cruiseLine.name)) {
      return `/cruise-line/${cruiseLine.name}`
    }

    return `/search/cruises?cruiseLines=${cruiseLine.code}`
  }, [vendorName])

  useEffect(() => {
    dispatch(fetchCruiseLines())
  }, [dispatch])

  const isCruiseOffer = holidayTypes?.includes('Cruises')

  return (
    <div className="OfferIntro">
      <TourInfo>
        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location">
            {location}{config.LE_TOURS_OFFER_PAGE_VIEW_MAP_BUTTON_ENABLED && <> • <TextLink href="#getting-there">view map</TextLink></>}
          </BodyTextBlock>
          <OfferTitle variant="heading2" forwardedAs="h1">
            {name}
          </OfferTitle>
        </VerticalSpacer>
        {(!cruiseLine && !vendorName) && lowestPricePackage.tour.logoImageId &&
          <TourLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={lowestPricePackage.tour.logoImageId}
            alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
          />}
        {(cruiseLine || vendorName) && lowestPricePackage.tour.logoImageId &&
          <Clickable to={getItemLink(cruiseLine)}>
            <TourLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={lowestPricePackage.tour.logoImageId}
              alt={`${lowestPricePackage.tour.name || 'Tour'} logo`}
            />
          </Clickable>}
      </TourInfo>

      <VerticalSpacer gap={16}>
        <div>
          {offer.badge && <OfferBadgeTagWrapper>
            <OfferBadgeTag badge={offer.badge} />
          </OfferBadgeTagWrapper>}
          {travelToDate && <TravelUntilMessageWrapper>
            <FlashTourTravelUntilMessage
              isCruise={isCruiseOffer}
              type={type}
              travelToDate={travelToDate}
              size="large"
            />
          </TravelUntilMessageWrapper>}
        </div>

        {isCruiseOffer && !offer.holidayTypes.includes('Ultra Lux') &&
          <CruiseSupportBanner />
        }
      </VerticalSpacer>
      {description && !offerPageTruncateText && <StyledOfferDescription offer={offer} description={description} />}
      {description && offerPageTruncateText && <StyledOfferDescription offer={offer} description={description} additionalDescription={additionalDescription} offerPageTruncateText />}
      {inclusions &&
        <>
          {inclusionsHeading && (
            <OfferHandPickInclusionsTitle variant="subtitle2">
              {inclusionsHeading}
            </OfferHandPickInclusionsTitle>
          )}
          <OfferMarkdownRender type="compact" content={inclusions} />
        </>
      }
      {additionalDescription && !offerPageTruncateText &&
        <OfferMarkdownRender type="compact" content={additionalDescription} />
      }
      <ButtonBar horizontal={vimeoVideoId ? 'between' : 'end'}>
        {vimeoVideoId && <TextButton
          kind="secondary"
          startIcon={<SolidPlayIcon />}
          onClick={showVideo}
        >
          Watch the video
        </TextButton>}
      </ButtonBar>

      {!isCruiseOffer && <ContactUSP />}
    </div>
  )
}

export default FlashTourIntro
